/**
 * TpUsdtSplitter — contract-side auto-split via deposit(amount).
 * Plain ERC20 transfer to the contract does NOT run Solidity; use approve+deposit.
 */
import { erc20Abi, type Address, type Hash, type PublicClient, type WalletClient } from 'viem';
import { bsc } from 'viem/chains';

export const TP_USDT_SPLITTER_ABI = [
  {
    type: 'function',
    name: 'token',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'address' }],
  },
  {
    type: 'function',
    name: 'deposit',
    stateMutability: 'nonpayable',
    inputs: [{ name: 'amount', type: 'uint256' }],
    outputs: [],
  },
] as const;

/** True when `depositAddress` is our splitter bound to `expectedToken`. */
export async function isTpUsdtSplitter(
  publicClient: PublicClient,
  depositAddress: Address,
  expectedToken: Address,
): Promise<boolean> {
  try {
    const token = (await publicClient.readContract({
      address: depositAddress,
      abi: TP_USDT_SPLITTER_ABI,
      functionName: 'token',
    })) as Address;
    return token.toLowerCase() === expectedToken.toLowerCase();
  } catch {
    return false;
  }
}

/**
 * Approve (if needed) + deposit — split 40/40/20 happens inside the deposit tx.
 * Returns the deposit transaction hash.
 */
export async function payUsdtViaSplitterDeposit(opts: {
  publicClient: PublicClient;
  walletClient: WalletClient;
  token: Address;
  splitter: Address;
  amountRaw: bigint;
  account: Address;
}): Promise<Hash> {
  const { publicClient, walletClient, token, splitter, amountRaw, account } = opts;

  const allowance = (await publicClient.readContract({
    address: token,
    abi: erc20Abi,
    functionName: 'allowance',
    args: [account, splitter],
  })) as bigint;

  if (allowance < amountRaw) {
    const approveHash = await walletClient.writeContract({
      address: token,
      abi: erc20Abi,
      functionName: 'approve',
      args: [splitter, amountRaw],
      account,
      chain: bsc,
    });
    const receipt = await publicClient.waitForTransactionReceipt({ hash: approveHash });
    if (receipt.status !== 'success') {
      throw new Error('USDT approve failed');
    }
  }

  return walletClient.writeContract({
    address: splitter,
    abi: TP_USDT_SPLITTER_ABI,
    functionName: 'deposit',
    args: [amountRaw],
    account,
    chain: bsc,
  });
}
